import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getAPIBaseURL, getLoginURL } from "../utils/api";
import { setAuthErrorHandler, onFallback } from "../utils/apiClient";
import { createConnectivityMonitor } from "./connectivity";
import { setConnectivityMonitor } from "./netFetch";
import { probeAuth } from "./boot";
import { subscribeSwUpdate, applySwUpdate, checkForSwUpdate, OFFLINE_CACHE_PREFIX } from "./swRegistration";
import { deleteOfflineDb } from "./db";
import { forgetWrap } from "./offlineKeys.js";
import { subscribe as subscribeSync, syncNow, initSyncState, resetSyncState, getSyncState } from "./syncManager";
import { installOfflineSearch } from "./searchSetup";
import { subscribe as subscribeFileSync, getFileSyncState, initFileSync, setFileSyncMode, startFileSync, abortFileSync, clearFileCache, refreshFileStats } from "./fileSyncManager";

const noop = () => {};
const DEFAULT_VALUE = {
  isOnline: true,
  env: null,
  tenant: null,
  offlineEnabled: false,
  swUpdate: { available: false },
  canInstall: false,
  syncState: getSyncState(),
  fileSync: getFileSyncState(),
  servedFromCacheAt: null,
  actions: { retryConnection: noop, applyUpdate: noop, install: noop, clearOfflineData: noop, syncNow: noop, setFileSyncMode: noop, syncFiles: noop, abortFileSync: noop, refreshFileStats: noop },
};

const OfflineContext = createContext(DEFAULT_VALUE);

export function useOffline() {
  return useContext(OfflineContext);
}

// Removes every per-tenant offline artefact except `od.lastEnv` (needed so the
// "noch keine Daten" page can still identify the tenant). SW stays registered.
export async function clearTenantOfflineData(tenant) {
  if (!tenant) return;
  if ("caches" in globalThis) {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((k) => (k.startsWith(OFFLINE_CACHE_PREFIX) || k.startsWith("odx-")) && k.endsWith(`-${tenant}`)).map((k) => caches.delete(k)),
    );
  }
  forgetWrap(tenant);                    // encrypted copy: the password-wrapped key goes too
  await clearFileCache(tenant);          // files + thumbnails + fileState; mode set to "none"
  await deleteOfflineDb(tenant);
  resetSyncState();
}

export function OfflineProvider({ initialOnline = true, env = null, children }) {
  const [isOnline, setIsOnline] = useState(!!initialOnline);
  const [swUpdate, setSwUpdate] = useState({ available: false });
  const [installEvent, setInstallEvent] = useState(null);
  const [syncState, setSyncState] = useState(getSyncState());
  const [fileSync, setFileSync] = useState(getFileSyncState());
  const [servedFromCacheAt, setServedFromCacheAt] = useState(null);
  const monitorRef = useRef(null);
  const bootedRef = useRef(false);

  const tenant = env?.TENANT || null;
  const offlineEnabled = env ? env.OFFLINE_ENABLED !== false : false;

  useEffect(() => {
    const base = getAPIBaseURL();
    const toLogin = () => {
      const loginUrl = getLoginURL() || env?.LOGIN_URL;
      if (loginUrl) window.location.href = loginUrl;
    };
    const monitor = createConnectivityMonitor({
      initialOnline,
      probe: () => probeAuth(base),
      onUnauthenticated: toLogin,
    });
    monitorRef.current = monitor;
    setConnectivityMonitor(monitor);
    setAuthErrorHandler(toLogin);
    const unsubscribe = monitor.subscribe(setIsOnline);
    return () => {
      unsubscribe();
      monitor.dispose();
      setConnectivityMonitor(null);
      setAuthErrorHandler(null);
      monitorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => subscribeSwUpdate((available) => setSwUpdate({ available })), []);
  useEffect(() => subscribeSync(setSyncState), []);
  useEffect(() => subscribeFileSync(setFileSync), []);
  useEffect(() => { if (tenant) initFileSync(tenant); }, [tenant]);
  useEffect(() => onFallback(() => setServedFromCacheAt(Date.now())), []);
  useEffect(() => { installOfflineSearch(); }, []);
  useEffect(() => { if (tenant) initSyncState(tenant); }, [tenant]);

  // Sync triggers: shortly after boot, and after every offline→online transition.
  useEffect(() => {
    if (!isOnline || !offlineEnabled || !tenant) return undefined;
    const reason = bootedRef.current ? "reconnect" : "boot";
    bootedRef.current = true;
    if (reason === "reconnect") checkForSwUpdate();
    const timer = setTimeout(() => { syncNow({ reason }).catch(() => {}); }, reason === "boot" ? 1500 : 2000);
    return () => clearTimeout(timer);
  }, [isOnline, offlineEnabled, tenant]);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => setInstallEvent(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const retryConnection = useCallback(() => monitorRef.current?.probe() ?? Promise.resolve(isOnline), [isOnline]);
  const install = useCallback(async () => {
    if (!installEvent) return false;
    installEvent.prompt();
    const choice = await installEvent.userChoice.catch(() => null);
    setInstallEvent(null);
    return choice?.outcome === "accepted";
  }, [installEvent]);
  const clearOfflineData = useCallback(() => clearTenantOfflineData(tenant), [tenant]);
  const triggerSync = useCallback((reason = "manual") => syncNow({ reason }), []);
  const fileActions = useMemo(() => ({
    setFileSyncMode: (mode) => setFileSyncMode(tenant, mode),
    syncFiles: () => startFileSync(tenant, { reason: "manual" }),
    abortFileSync,
    refreshFileStats: () => refreshFileStats(tenant),
  }), [tenant]);

  const value = useMemo(
    () => ({
      isOnline,
      env,
      tenant,
      offlineEnabled,
      swUpdate,
      canInstall: !!installEvent,
      syncState,
      fileSync,
      servedFromCacheAt,
      actions: { retryConnection, applyUpdate: applySwUpdate, install, clearOfflineData, syncNow: triggerSync, ...fileActions },
    }),
    [isOnline, env, tenant, offlineEnabled, swUpdate, installEvent, syncState, fileSync, servedFromCacheAt, retryConnection, install, clearOfflineData, triggerSync, fileActions],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export default OfflineProvider;
